import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Redirect } from 'react-router-dom';
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

import DatePicker from 'react-datepicker/es';
import 'react-datepicker/dist/react-datepicker.css';

import Creatable from 'react-select/creatable';

import { FcCancel } from 'react-icons/fc';

import FileUpload from '../components/FileUpload';
import './UploadPage.css';
import { useAppState } from '../AppContext';
import AppNavbar from '../components/Navbar';

import { enableUploads } from '../components/utils';

const STATUSES = { LOADING: 'LOADING', SUCCESS: 'SUCCESS', ERROR: 'ERROR' };

const UploadComplete = ({ sharingAllowed, fileList }) => {
  const file = fileList.length > 0 ? 'files' : 'file';
  return (
    <>
      <AppNavbar />
      <Row className="limitedWidth">
        <h2 style={{ textAlign: 'center' }}>Upload completed</h2>
      </Row>

      <Row className="limitedWidth">
        <Col>
          <p>Thank you for helping with our research!</p>
          {sharingAllowed ? (
            <p>
              You have been sent a confirmation email to verify your email
              address. As you indicated that the {file} that you uploaded can be
              publicly shared, they will appear on this website once they have
              been checked by a moderator and you have confirmed your email
              address.
            </p>
          ) : (
            <p>
              You have been sent a confirmation email to verify your email
              address. As you indicated that the {file} that you uploaded{' '}
              <b>should not</b> be publicly shared, they will not appear on this
              website, but they will be used to help develop and test
              text-processing tools.
            </p>
          )}
          The files that you uploaded were:
          <ol>
            {fileList.map((f) => (
              <li key={f.path}>{f.path}</li>
            ))}
          </ol>
          <p>
            <a href="/">
              <Button>Return to main page.</Button>
            </a>
          </p>
        </Col>
      </Row>
    </>
  );
};

UploadComplete.propTypes = {
  sharingAllowed: PropTypes.bool,
  fileList: PropTypes.arrayOf(PropTypes.shape({ path: PropTypes.string }))
};

function UploadPage() {
  const [date, setDate] = useState('');
  const [orgLevel, setOrgLevel] = useState('Local');
  const [orgName, setOrgName] = useState('');

  const [status, setStatus] = useState();
  const [hospitals, setHospitals] = useState(false);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [useAllowed, setUseAllowed] = useState(false);
  const [sharingAllowed, setSharingAllowed] = useState(false);

  const [{ fileList, uploadTitle }, dispatch] = useAppState();

  // fetch documents
  useEffect(() => {
    fetch('/api/_autocomplete')
      .then((res) => res.json())
      .then((data) => {
        setHospitals(data);
      });
  }, []);

  if (enableUploads) {
    return <Redirect to={'/'} />;
  }

  const uploadButton = () => {
    const formData = new FormData(document.getElementById('uploadform'));

    setStatus(STATUSES.LOADING);
    fileList.forEach((file) => formData.append('filetoupload', file));

    if (fileList.length > 1) {
      formData.append('edit_date', date && date.toISOString().substr(0, 10));
    }

    formData.append('org_level', orgLevel);
    formData.append('org_name', orgName);
    formData.append('public', sharingAllowed);

    fetch('/api/documents/upload', {
      method: 'POST',
      body: formData
    })
      .then((response) => response.json())
      .then((result) => {
        console.log('Success:', result);
        setStatus(STATUSES.SUCCESS);
      })
      .catch((error) => {
        console.error('Error:', error);
        setStatus(STATUSES.ERROR);
      });
  };

  const selectOrg = (opt) => {
    setOrgName(!opt ? false : opt.value);

    // set org_type if set
    if (opt.type) {
      setOrgLevel(opt.type);
    }
  };

  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const validateEmail = (email) => emailRegex.test(String(email).toLowerCase());

  let formComplete;
  if (fileList.length === 0) {
    formComplete = false;
  } else if (useAllowed && !sharingAllowed) {
    formComplete = true;
  } else if (
    useAllowed &&
    authorized &&
    name &&
    email &&
    validateEmail(email)
  ) {
    formComplete = true;
  }

  if (status === STATUSES.SUCCESS) {
    return (
      <UploadComplete sharingAllowed={sharingAllowed} fileList={fileList} />
    );
  }

  return (
    <>
      <AppNavbar />
      <Row className="limitedWidth">
        <h2 style={{ textAlign: 'center' }}>Guideline Upload</h2>
      </Row>

      <Row className="limitedWidth">
        <Col>
          <p>
            This site was to created by the{' '}
            <a href="https://isaric4c.net/">ISARIC-4C</a> consortium to help
            clinicians browse and share guidelines for treating patients with
            COVID-19.
          </p>

          <p>
            Note that uploaded guidelines will not be publicly visible until
            they have been approved by a moderator.
          </p>

          <Form
            method="POST"
            name="uploadform"
            encType="multipart/form-data"
            id="uploadform"
          >
            <div
              style={{
                background: 'hsl(212, 80%, 95%)',
                borderRadius: '10px',
                padding: '10px',
                marginTop: '20px'
              }}
            >
              <FileUpload />

              {fileList.length === 1 && (
                <>
                  <h5>Optional document metadata</h5>
                  <Form.Group as={Row} controlId="title">
                    <Form.Label column sm="2">
                      Title:
                    </Form.Label>
                    <Col sm="10">
                      <Form.Control
                        type="text"
                        name="title"
                        value={uploadTitle}
                        onChange={(ev) =>
                          dispatch({
                            type: 'SET_UPLOAD_TITLE',
                            uploadTitle: ev.target.value
                          })
                        }
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} controlId="version">
                    <Form.Label column sm="2">
                      Version:
                    </Form.Label>
                    <Col sm="10">
                      <Form.Control type="text" name="version" />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} controlId="edit_date">
                    <Form.Label column sm="2">
                      Date last edited:
                    </Form.Label>
                    <Col sm="10">
                      <DatePicker
                        selected={date}
                        dateFormat="yyyy-MM-dd"
                        onChange={(date) => setDate(date)}
                      />{' '}
                      {date && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setDate('')}
                        >
                          <FcCancel />
                        </Button>
                      )}
                    </Col>
                  </Form.Group>
                </>
              )}

              {fileList.length > 1 && (
                <p>
                  Form inputs to set document title and date are not shown as
                  more than one file has been selected for upload.
                </p>
              )}
            </div>

            <div
              style={{
                background: 'hsl(212, 80%, 95%)',
                borderRadius: '10px',
                padding: '10px',
                marginTop: '20px'
              }}
            >
              <Form.Group as={Row} controlId="org_name">
                <Form.Label column sm="2">
                  Organisation name:
                </Form.Label>
                <Col sm="10">
                  <Creatable
                    options={hospitals}
                    onChange={(opt) => {
                      selectOrg(opt);
                    }}
                    value={orgName ? { value: orgName, label: orgName } : false}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="org_level">
                <Form.Label column sm="2">
                  Organisation level:
                </Form.Label>
                <Col sm="10">
                  <Form.Control
                    as="select"
                    name="org_level"
                    onChange={(ev) => {
                      setOrgLevel(ev.target.value);
                    }}
                    value={orgLevel}
                  >
                    <option>Local</option>
                    <option>National</option>
                    <option>International Organsiation</option>
                  </Form.Control>
                </Col>
              </Form.Group>
            </div>

            <div
              style={{
                background: 'hsl(212, 80%, 95%)',
                borderRadius: '10px',
                padding: '10px',
                marginTop: '20px'
              }}
            >
              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  id={`allow-use`}
                  className="required"
                >
                  <Form.Check.Input
                    type="checkbox"
                    onChange={(ev) => setUseAllowed(ev.target.checked)}
                    checked={useAllowed}
                  />
                  <Form.Check.Label>
                    {
                      <>
                        I agree to allow these documents to be{' '}
                        <b>used for research</b> about techniques for extracting
                        information from clinical guidelines.{' '}
                      </>
                    }
                  </Form.Check.Label>
                  <Form.Text className="text-muted">
                    Developing robust methods requires access to a large
                    collection of examples that use as wide a variety of
                    formatting as possible, so uploading guidelines will help
                    our research even if you cannot allow us to share them with
                    others.
                  </Form.Text>
                </Form.Check>
              </div>

              <div className="mb-3">
                <Form.Check type="checkbox" id="public">
                  <Form.Check.Input
                    type="checkbox"
                    onChange={(ev) => setSharingAllowed(ev.target.checked)}
                  />
                  <Form.Check.Label>
                    {
                      <>
                        I agree to allow these documents to be{' '}
                        <b>publicly shared</b>.{' '}
                      </>
                    }
                  </Form.Check.Label>
                  <Form.Text className="text-muted">
                    By allowing the guidelines that you upload to be publicly
                    shared, you can directly help other clinicians who are
                    writing their own local guidelines.{' '}
                  </Form.Text>
                </Form.Check>
              </div>

              {sharingAllowed && (
                <>
                  <div className="mb-3">
                    <Form.Check
                      className="required"
                      id={'declare_authorized'}
                      label={
                        'I declare that I am authorized to upload these documents.'
                      }
                      onChange={(ev) => setAuthorized(ev.target.checked)}
                      checked={authorized}
                    />
                  </div>

                  <br />
                  <Form.Group className="required" as={Row} controlId="name">
                    <Form.Label column sm="2">
                      Name:
                    </Form.Label>
                    <Col sm="10">
                      <Form.Control
                        type="text"
                        name="name"
                        value={name}
                        onChange={(ev) => setName(ev.target.value)}
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group className="required" as={Row} controlId="email">
                    <Form.Label
                      column
                      sm="2"
                      className={
                        email && !validateEmail(email) ? 'invalidEmail' : ''
                      }
                    >
                      Work Email address:
                    </Form.Label>
                    <Col sm="10">
                      <Form.Control
                        type="email"
                        name="email"
                        value={email}
                        onChange={(ev) => {
                          setEmail(ev.target.value);
                        }}
                      />
                      <Form.Text className="text-muted">
                        An email containing a link will be sent to this address:
                        you must click on this link to confirm your identity
                        before the uploaded documents will be shared.
                      </Form.Text>
                    </Col>
                  </Form.Group>
                </>
              )}
              <p>
                Before uploading, it is your resposibility to ensure that any
                personally identifiable information or other sensitive details
                (e.g, account billing codes) have been appropriately redacted.
              </p>
            </div>
          </Form>

          <Button
            variant="primary"
            type="submit"
            onClick={(ev) => uploadButton(ev)}
            disabled={!formComplete}
            style={{ marginTop: '20px' }}
          >
            {status === STATUSES.LOADING ? (
              <>
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                Uploading...
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </Col>
      </Row>
    </>
  );
}

export default UploadPage;
